import { useEffect, useState } from "react";
import { X, ShoppingBag, ArrowRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "../lib/cart";
import { trackEvent } from "../lib/analytics";

/**
 * Silent cart-recovery banner — slides in from the bottom for returning
 * visitors who have items in their localStorage cart but landed on a
 * non-cart page. Covers the ~70% of abandoning visitors who never enter
 * email (so the email-based recovery flow can't reach them).
 *
 *  - Activates if cart has 1+ items AND user is NOT on /cart or /checkout
 *  - Suppressed for 24h after dismiss (localStorage)
 *  - Suppressed during current session if they visit /cart
 *  - Hidden on /admin (different user context)
 *  - Mobile: anchored bottom + spacer for sticky CTA bar
 */
const KEY = "tc_cart_banner_dismissed_v1";
const DISMISS_MS = 24 * 60 * 60 * 1000;
const HIDE_PREFIXES = ["/admin", "/cart", "/checkout", "/success"];

export default function CartRecoveryBanner() {
  const { items } = useCart();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  const hidden = HIDE_PREFIXES.some((p) => pathname.startsWith(p));
  const cartCount = items?.length || 0;
  const cartTotal = (items || []).reduce(
    (sum, it) => sum + Number(it.unit_price || 0) * Number(it.quantity || 1),
    0,
  );

  useEffect(() => {
    if (hidden || cartCount === 0) {
      setShow(false);
      return;
    }
    try {
      const dismissed = localStorage.getItem(KEY);
      if (dismissed && Date.now() - Number(dismissed) < DISMISS_MS) return;
    } catch { /* no-op */ }
    // Show after 3s so it doesn't fight with hero animations
    const t = setTimeout(() => {
      setShow(true);
      trackEvent("cart_recovery_banner_shown", { items: cartCount, value: cartTotal });
    }, 3000);
    return () => clearTimeout(t);
  }, [hidden, cartCount, cartTotal]);

  const dismiss = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch { /* no-op */ }
    setShow(false);
  };

  if (!show || hidden || cartCount === 0) return null;

  return (
    <div
      data-testid="cart-recovery-banner"
      className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 surface-card p-4 shadow-2xl animate-[slideUp_300ms_ease-out]"
      role="alert"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss cart reminder"
        data-testid="cart-banner-close"
        className="absolute top-2 right-2 text-zinc-500 hover:text-white"
      >
        <X size={14} />
      </button>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 bg-[var(--tc-red)] flex items-center justify-center">
          <ShoppingBag size={18} />
        </div>
        <div>
          <div className="font-ui font-bold text-sm uppercase tracking-wide">
            Don't forget your cart
          </div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
            {cartCount} item{cartCount !== 1 ? "s" : ""} · ${cartTotal.toFixed(2)}
          </div>
        </div>
      </div>
      <Link
        to="/cart"
        onClick={() => {
          trackEvent("cart_recovery_banner_click", { items: cartCount, value: cartTotal });
          setShow(false);
        }}
        data-testid="cart-banner-cta"
        className="btn-tc-primary w-full inline-flex items-center justify-center gap-2 text-xs py-3"
      >
        Continue Checkout <ArrowRight size={14} />
      </Link>
    </div>
  );
}
